import { Button, Css, IconButton, Palette, Tag, Tooltip, useModal, useTestIds } from "@homebound/beam";
import { useContext } from "react";
import { isDefined } from "src/utils";
import { designUpgradeTagColor, planPackageTagColor } from "../../designCatalogAtoms";
import { ColumnView } from "../DesignPackageCompareMode";
import { DesignPackageCompareModeContext } from "../DesignPackageCompareModeContext";
import { CompareCell } from "./CompareCell";
import { CompareConfigModal } from "./CompareConfigModal";

type CompareColumnProps = { columnIndex: number; numGridColumns: ColumnView };

export function CompareColumn(props: CompareColumnProps) {
  const { columnIndex, numGridColumns } = props;
  const openConfigModal = useCompareConfigModal();
  const { columns, removeColumn, slots, tlis, designPackage } = useContext(DesignPackageCompareModeContext);
  const tid = useTestIds({}, `column${columnIndex}`);

  const thisColRpoIds = columns[columnIndex];
  const columnOptions = thisColRpoIds?.map((rpoId) => designPackage.options.find((rpo) => rpo.id === rpoId)).compact();

  /**
   * Because we allow users to toggle between a single column view and a grid view, we need to adjust the column width.
   *
   * Only when single column view is enabled, if the total width of columns (each initially 480px) exceeds the window's width,
   * set their width to 345px to fit more columns on the screen.
   */
  const columnMaxWidth =
    numGridColumns === ColumnView.Single ? (columns.length * 480 > window.innerWidth ? 345 : 480) : 724;

  const tlisBySlot = thisColRpoIds?.isEmpty
    ? [] // brand new col: thisCol = `[]` and `[empty].every() ==> true`, so skip filtering
    : slots.map((placeholderId) =>
        tlis
          // restrict all TLIs down to just the product family of cards for this placeholder tli
          .filter((tli) => tli.placeholder?.id === placeholderId)
          // find which one (or more?) of those apply to the current column of options
          // The target combination options must be a subset of the column options
          .filter((tli) => tli.targetCombinations.some((tc) => tc.options.every((o) => thisColRpoIds.includes(o.id)))),
      );

  return (
    <div
      {...tid}
      css={
        Css.bgWhite.br8.fg1.fs1
          .fb("auto")
          .maxwPx(columnMaxWidth)
          .mwPx(numGridColumns === ColumnView.Single ? 345 : 724).$
      }
    >
      <div /* Column Header */ css={Css.df.jcsb.p2.bb.bcGray300.$}>
        {tlisBySlot.nonEmpty ? (
          <div css={Css.df.gap1.$}>
            {columnOptions.map((rpo) => {
              const bgColor = rpo.optionGroup.isPlanPackage
                ? planPackageTagColor
                : rpo.optionGroup.forDesignUpgrade
                  ? designUpgradeTagColor
                  : rpo.optionGroup.group.designPackageTagColor;
              return (
                <div key={rpo.id} css={Css.py1.$}>
                  <Tag
                    xss={Css.xsSb.if(isDefined(bgColor)).bgColor(bgColor!).$ as any}
                    text={rpo.name.replace(/[aeiou]/g, "").slice(0, 3)}
                  />
                </div>
              );
            })}
          </div>
        ) : (
          <div css={Css.xl.$}>Column {columnIndex + 1}</div>
        )}
        <div css={Css.df.gap1.$}>
          {tlisBySlot.nonEmpty ? (
            <IconButton icon="cog" onClick={() => openConfigModal(columnIndex)} />
          ) : (
            <Button label="Configure" icon="cog" onClick={() => openConfigModal(columnIndex)} />
          )}
          <IconButton icon="trash" color={Palette.Gray500} onClick={() => removeColumn(columnIndex)} />
        </div>
      </div>
      <div /* Column TLIs */ css={Css.bgWhite.p2.br8.gap2.dg.gtc(`repeat(${numGridColumns}, 1fr)`).jic.aic.$}>
        {tlisBySlot.nonEmpty ? (
          tlisBySlot.map((tlis, i) => (
            <Tooltip
              key={tlis.first?.id ?? `empty-slot-${i}`}
              title={<div>{tlis.first?.materialVariant?.displayName ?? "No Product Selected"}</div>}
            >
              <div {...tid.row}>
                <CompareCell tlis={tlis} />
              </div>
            </Tooltip>
          ))
        ) : (
          <EmptyState columnView={numGridColumns} />
        )}
      </div>
    </div>
  );
}

function EmptyState(props: { columnView?: ColumnView }) {
  const showGrid = props.columnView === ColumnView.Grid;
  // If grid column view, show 6 slots, otherwise show 3 slots
  const numberOfSlots = showGrid ? 6 : 3;
  return Array.from({ length: numberOfSlots }).map((i) => (
    <div data-testid="emptyStateSlot" key={`empty-slot-${i}`} css={Css.hPx(220).wPx(220).bgGray200.br4.$} />
  ));
}

export function useCompareConfigModal() {
  const ctx = useContext(DesignPackageCompareModeContext);
  const { openModal } = useModal();

  return (columnIndex: number) => {
    openModal({
      content: (
        <DesignPackageCompareModeContext.Provider value={ctx}>
          <CompareConfigModal columnIndex={columnIndex} />
        </DesignPackageCompareModeContext.Provider>
      ),
    });
  };
}
